import React, { useState, useEffect, useCallback } from 'react'
import { 
  Card, 
  Select, 
  Button, 
  Typography, 
  Tag, 
  message,
  Spin,
  Empty,
  Modal,
  List
} from 'antd'
import { 
  ArrowLeftOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  CarOutlined,
  GiftOutlined,
  UndoOutlined,
  SortAscendingOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { receiptAPI } from '../services/api'
import './ReceiptViewPage.css'

const { Title, Text } = Typography
const { Option } = Select

interface ReceiptItem {
  id: string
  type: 'test_drive' | 'delivery'
  type_name: string
  customer_name?: string
  series_id: number
  series_name: string
  vehicle_model?: string
  vehicle_color?: string
  vehicle_quantity?: number
  delivery_standard_id?: number
  delivery_standard_name?: string
  appointment_time?: string
  receipt_status: number
  receipt_status_text: string
  reply_time?: string
  created_at: string
  /** 后端格式化的东八区展示时间 MM/DD HH:mm，优先展示 */
  created_at_display?: string
}

interface ReceiptStats {
  total: number
  test_drive_total: number
  delivery_total: number
  unread: number
  read_unreplied: number
  replied: number
  test_drive_unread: number
  test_drive_read_unreplied: number
  test_drive_replied: number
  delivery_unread: number
  delivery_read_unreplied: number
  delivery_replied: number
}

interface FilterOptions {
  receipt_status_options: Array<{value: number | null, label: string}>
  invitation_type_options: Array<{value: string | null, label: string}>
  time_sort_options: Array<{value: string, label: string}>
}

const ReceiptViewPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(false)
  const [receipts, setReceipts] = useState<ReceiptItem[]>([])
  const [stats, setStats] = useState<ReceiptStats | null>(null)
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptItem | null>(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [sortModalVisible, setSortModalVisible] = useState(false)
  const [tempTimeSort, setTempTimeSort] = useState<string>('created_desc')
  const [resendingReceiptId, setResendingReceiptId] = useState<string | null>(null)
  
  // 筛选状态
  const [receiptStatus, setReceiptStatus] = useState<number | null>(null)
  const [invitationType, setInvitationType] = useState<string | null>(null)
  const [timeSort, setTimeSort] = useState<string>('created_desc')
  
  // 获取筛选选项
  const fetchFilterOptions = async () => {
    try {
      const response = await receiptAPI.getFilterOptions()
      
      if ((response as any).success) {
        setFilterOptions((response as any).data)
      }
    } catch (error) {
      // 静默处理错误
    }
  }
  
  // 获取回执列表
  const fetchReceipts = useCallback(async (page: number = 1, append: boolean = false) => {
    if (!user) {
      return
    }
    
    setLoading(true)
    try {
      const params = {
        receiptStatus,
        invitationType,
        timeSort,
        limit: 10,
        page
      }
      
      const response = await receiptAPI.getReceiptsList(params)
      
      if ((response as any).success) {
        const newReceipts = (response as any).data.receipts || []
        if (append) {
          setReceipts(prev => [...prev, ...newReceipts])
        } else {
          setReceipts(newReceipts)
        }
        setStats((response as any).data.stats)
        setHasMore(newReceipts.length === 10)
        setCurrentPage(page)
      } else {
        message.error((response as any).message || '获取回执列表失败')
      }
    } catch (error) {
      message.error('获取回执列表失败')
    } finally {
      setLoading(false)
    }
  }, [user, receiptStatus, invitationType, timeSort])
  
  useEffect(() => {
    fetchFilterOptions()
    fetchReceipts()
  }, [user])
  
  // 当筛选条件变化时重新获取数据
  useEffect(() => {
    if (filterOptions) {
      const fetchData = async () => {
        if (!user) return
        
        setReceipts([])
        setCurrentPage(1)
        setHasMore(true)
        setLoading(true)
        
        try {
          const params = {
            receiptStatus,
            invitationType,
            timeSort,
            limit: 10,
            page: 1
          }
          
          const response = await receiptAPI.getReceiptsList(params)
          
          if ((response as any).success) {
            const newReceipts = (response as any).data.receipts || []
            setReceipts(newReceipts)
            setStats((response as any).data.stats)
            setHasMore(newReceipts.length === 10)
            setCurrentPage(1)
          } else {
            message.error((response as any).message || '获取回执列表失败')
          }
        } catch (error) {
          message.error('获取回执列表失败')
        } finally {
          setLoading(false)
        }
      }
      
      fetchData()
    }
  }, [receiptStatus, invitationType, timeSort, filterOptions, user])
  
  // 加载更多数据
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && user) {
      const loadMoreData = async () => {
        setLoading(true)
        try {
          const params = {
            receiptStatus,
            invitationType,
            timeSort,
            limit: 10,
            page: currentPage + 1
          }
          
          const response = await receiptAPI.getReceiptsList(params)
          
          if ((response as any).success) {
            const newReceipts = (response as any).data.receipts || []
            setReceipts(prev => [...prev, ...newReceipts])
            setHasMore(newReceipts.length === 10)
            setCurrentPage(currentPage + 1)
          } else {
            message.error((response as any).message || '获取回执列表失败')
          }
        } catch (error) {
          message.error('获取回执列表失败')
        } finally {
          setLoading(false)
        }
      }
      
      loadMoreData()
    }
  }, [loading, hasMore, user, receiptStatus, invitationType, timeSort, currentPage])
  
  // 重置筛选条件
  const handleResetFilters = () => {
    setReceiptStatus(null)
    setInvitationType(null)
    setTimeSort('created_desc')
  }
  
  // 查看详情
  const handleViewDetail = (receipt: ReceiptItem) => {
    setSelectedReceipt(receipt)
    setDetailModalVisible(true)
  }

  // 重发消息
  const handleResendMessage = async (receipt: ReceiptItem) => {
    if (!user) {
      message.error('用户未登录')
      return
    }

    setResendingReceiptId(receipt.id)
    try {
      const response = await receiptAPI.resendMessage(receipt.id, receipt.type)
      
      if ((response as any).success) {
        message.success('邀请函已发送到企业微信！')
      } else {
        message.error((response as any).message || '发送失败，请稍后重试')
      }
    } catch (error) {
      console.error('重发企业微信消息失败:', error)
      message.error('发送失败，请稍后重试')
    } finally {
      setResendingReceiptId(null)
    }
  }
  
  // 打开排序选择
  const handleOpenSortModal = () => {
    setTempTimeSort(timeSort)
    setSortModalVisible(true)
  }
  
  // 确认排序选择
  const handleConfirmSort = () => {
    setTimeSort(tempTimeSort)
    setSortModalVisible(false)
  }
  
  // 取消排序选择
  const handleCancelSort = () => {
    setTempTimeSort(timeSort)
    setSortModalVisible(false)
  }
  
  // 获取状态标签颜色
  const getStatusTagColor = (status: number) => {
    switch (status) {
      case 0: return 'default'  // 未回执
      case 1: return 'success'  // 已回执
      case 2: return 'processing'  // 已读未回执
      default: return 'default'
    }
  }
  
  // 获取状态图标
  const getStatusIcon = (status: number) => {
    switch (status) {
      case 0: return <ClockCircleOutlined />
      case 1: return <CheckCircleOutlined />
      case 2: return <EyeOutlined />
      default: return <ClockCircleOutlined />
    }
  }
  
  // 获取类型图标
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'test_drive': return <CarOutlined />
      case 'delivery': return <GiftOutlined />
      default: return <CarOutlined />
    }
  }
  
  // 格式化时间（预约/回执等，按本地习惯解析）
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '-'
    const date = new Date(timeStr)
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  
  return (
    <div className="receipt-view-page">
      {/* 顶部导航 */}
      <div className="page-header">
        <div className="header-content">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard')}
            className="back-btn"
          >
            返回
          </Button>
          <Title level={4} className="page-title">回执查看</Title>
          <div className="header-spacer"></div>
        </div>
      </div>

      {/* 页面内容 */}
      <div className="page-content">
        {/* 统计信息 */}
        {stats && (
          <Card className="stats-card" size="small">
            <div className="stats-row">
              <div className="stat-item">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">总邀请函</span>
              </div>
              <div className="stat-divider">|</div>
              <div className="stat-item">
                <span className="stat-number">{stats.unread}</span>
                <span className="stat-label">未回执</span>
              </div>
              <div className="stat-divider">|</div>
              <div className="stat-item">
                <span className="stat-number">{stats.read_unreplied}</span>
                <span className="stat-label">已读未回执</span>
              </div>
              <div className="stat-divider">|</div>
              <div className="stat-item">
                <div className="stat-number">{stats.replied}</div>
                <div className="stat-label">已回执</div>
              </div>
            </div>
          </Card>
        )}

        {/* 筛选器 */}
        <Card className="form-card">
          <div className="filter-section">
            <div className="filter-header">
              <Text strong className="form-label">
                <FilterOutlined className="label-icon" />
                筛选条件
              </Text>
              <div className="filter-header-actions">
                <Button 
                  type="default"
                  icon={<SortAscendingOutlined />}
                  onClick={handleOpenSortModal}
                  className="sort-btn"
                  title="排序"
                />
                <Button 
                  type="default"
                  icon={<UndoOutlined />}
                  onClick={handleResetFilters}
                  className="reset-btn"
                  title="重置筛选"
                />
              </div>
            </div>
            
            <div className="filter-row">
              <div className="filter-item">
                <Text className="filter-label">回执状态</Text>
                <Select
                  value={receiptStatus}
                  onChange={setReceiptStatus}
                  placeholder="全部"
                  className="form-select mobile-select"
                  size="large"
                  dropdownStyle={{
                    fontSize: '16px',
                    minHeight: '48px'
                  }}
                >
                  {filterOptions?.receipt_status_options.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
              
              <div className="filter-item">
                <Text className="filter-label">邀请函类型</Text>
                <Select
                  value={invitationType}
                  onChange={setInvitationType}
                  placeholder="全部"
                  className="form-select mobile-select"
                  size="large"
                  dropdownStyle={{
                    fontSize: '16px',
                    minHeight: '48px'
                  }}
                >
                  {filterOptions?.invitation_type_options.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* 邀请函列表 */}
        <Card className="form-card">
          <div className="list-header">
            <Title level={5} className="list-title">邀请函列表</Title>
          </div>
          
          {loading && receipts.length === 0 ? (
            <div className="loading-container">
              <Spin size="large" />
              <Text className="loading-text">加载中...</Text>
            </div>
          ) : receipts.length === 0 ? (
            <Empty description="暂无邀请函数据" />
          ) : (
            <div className="receipt-list">
              <List
                dataSource={receipts}
                renderItem={(receipt) => (
                  <List.Item key={receipt.id} className="receipt-list-item">
                    <div className="receipt-mobile-item">
                      {/* 客户姓名和操作按钮 */}
                      <div className="receipt-customer-row">
                        <Text strong className="customer-name">
                          {receipt.customer_name || '未填写'}
                        </Text>
                        <div className="receipt-actions">
                          <Button
                            type="default"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleResendMessage(receipt)
                            }}
                            className="resend-btn"
                            loading={resendingReceiptId === receipt.id}
                          >
                            重发
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleViewDetail(receipt)
                            }}
                            className="view-detail-btn"
                          >
                            查看
                          </Button>
                        </div>
                      </div>
                      
                      {/* 邀请函类型和回执状态 */}
                      <div className="receipt-type-status-row">
                        <div className="receipt-type-info">
                          {getTypeIcon(receipt.type)}
                          <Text className="type-text">{receipt.type_name}</Text>
                        </div>
                        <div className="receipt-status-info">
                          <Tag 
                            color={getStatusTagColor(receipt.receipt_status)}
                            icon={getStatusIcon(receipt.receipt_status)}
                            className="status-tag-small"
                          >
                            {receipt.receipt_status_text}
                          </Tag>
                        </div>
                      </div>
                      
                      {/* 创建和预约时间 */}
                      <div className="receipt-time-row">
                        <div className="receipt-time-info">
                          <Text className="time-label">创建:</Text>
                          <Text className="time-value">{receipt.created_at_display ?? receipt.created_at}</Text>
                        </div>
                        <div className="receipt-time-info">
                          <Text className="time-label">预约:</Text>
                          <Text className="time-value">
                            {receipt.appointment_time ? formatTime(receipt.appointment_time) : '未设置'}
                          </Text>
                        </div>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
              
              {/* 加载更多按钮 */}
              {hasMore && (
                <div className="load-more-container">
                  <Button
                    type="default"
                    size="large"
                    onClick={handleLoadMore}
                    loading={loading}
                    className="load-more-btn"
                    block
                  >
                    {loading ? '加载中...' : '加载更多'}
                  </Button>
                </div>
              )}
              
              {/* 没有更多结果提示 */}
              {!hasMore && receipts.length > 0 && (
                <div className="no-more-container">
                  <Text type="secondary">没有更多数据了</Text>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
      
      {/* 详情弹窗 */}
      <Modal
        title="邀请函详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: '600px' }}
      >
        {selectedReceipt && (
          <div className="receipt-detail">
            <div className="detail-section">
              <Title level={5}>基本信息</Title>
              <div className="detail-grid">
                <div className="detail-item">
                  <Text className="detail-label">客户姓名:</Text>
                  <Text className="detail-value">{selectedReceipt.customer_name || '未填写'}</Text>
                </div>
                <div className="detail-item">
                  <Text className="detail-label">邀请函类型:</Text>
                  <Text className="detail-value">
                    {getTypeIcon(selectedReceipt.type)}
                    {selectedReceipt.type_name}
                  </Text>
                </div>
                <div className="detail-item">
                  <Text className="detail-label">回执状态:</Text>
                  <Tag 
                    color={getStatusTagColor(selectedReceipt.receipt_status)}
                    icon={getStatusIcon(selectedReceipt.receipt_status)}
                  >
                    {selectedReceipt.receipt_status_text}
                  </Tag>
                </div>
                <div className="detail-item">
                  <Text className="detail-label">车系:</Text>
                  <Text className="detail-value">{selectedReceipt.series_name}</Text>
                </div>
                {/* 车型信息 - 交车邀请函显示车型，试乘试驾邀请函不显示 */}
                {selectedReceipt.type === 'delivery' && selectedReceipt.vehicle_model && (
                  <div className="detail-item">
                    <Text className="detail-label">车型:</Text>
                    <Text className="detail-value">{selectedReceipt.vehicle_model}</Text>
                  </div>
                )}
                {/* 车辆颜色 - 只有交车邀请函显示 */}
                {selectedReceipt.type === 'delivery' && selectedReceipt.vehicle_color && (
                  <div className="detail-item">
                    <Text className="detail-label">车辆颜色:</Text>
                    <Text className="detail-value">{selectedReceipt.vehicle_color}</Text>
                  </div>
                )}
                {/* 车辆数量 - 只有交车邀请函显示 */}
                {selectedReceipt.type === 'delivery' && selectedReceipt.vehicle_quantity && (
                  <div className="detail-item">
                    <Text className="detail-label">车辆数量:</Text>
                    <Text className="detail-value">{selectedReceipt.vehicle_quantity}辆</Text>
                  </div>
                )}
                {/* 交车标准 - 只有交车邀请函且已回执时显示 */}
                {selectedReceipt.type === 'delivery' && selectedReceipt.receipt_status === 1 && selectedReceipt.delivery_standard_name && (
                  <div className="detail-item">
                    <Text className="detail-label">选择交车标准:</Text>
                    <Text className="detail-value">{selectedReceipt.delivery_standard_name}</Text>
                  </div>
                )}
              </div>
            </div>
            
            <div className="detail-section">
              <Title level={5}>时间信息</Title>
              <div className="detail-grid">
                <div className="detail-item">
                  <Text className="detail-label">创建时间:</Text>
                  <Text className="detail-value">{selectedReceipt.created_at_display ?? selectedReceipt.created_at}</Text>
                </div>
                {selectedReceipt.appointment_time && (
                  <div className="detail-item">
                    <Text className="detail-label">预约时间:</Text>
                    <Text className="detail-value">{formatTime(selectedReceipt.appointment_time)}</Text>
                  </div>
                )}
                {selectedReceipt.reply_time && (
                  <div className="detail-item">
                    <Text className="detail-label">回执时间:</Text>
                    <Text className="detail-value">{formatTime(selectedReceipt.reply_time)}</Text>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
      
      {/* 排序选择弹窗 */}
      <Modal
        title="选择排序方式"
        open={sortModalVisible}
        onCancel={handleCancelSort}
        footer={[
          <Button key="cancel" onClick={handleCancelSort}>
            取消
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmSort}>
            确认
          </Button>
        ]}
        width="90%"
        style={{ maxWidth: '400px' }}
      >
        <div className="sort-options">
          {filterOptions?.time_sort_options.map(option => (
            <div 
              key={option.value} 
              className={`sort-option ${tempTimeSort === option.value ? 'selected' : ''}`}
              onClick={() => setTempTimeSort(option.value)}
            >
              <Text>{option.label}</Text>
              {tempTimeSort === option.value && <CheckCircleOutlined className="check-icon" />}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default ReceiptViewPage
